'use client'

// eslint-disable-next-line no-restricted-imports
import { usePathname, useSearchParams } from 'next/navigation'
import { useEffect } from 'react'

import { useRouter } from '@/i18n/navigation'
import { useLocale } from '@/shared/locale'
import { routes } from '@/shared/routes'
import { useGlobalStore } from '@/shared/store/provider'
import { logError } from '@/shared/utils/error'
import { PageError } from '@/shared/utils/errorClasses'

export type QueryParams = 'eurobonus' | 'coop'
export const queryMapping: Record<QueryParams, string> = {
  eurobonus: 'partnerInfo',
  coop: 'coopMembershipId',
}

export const QueryHandler: React.FC = () => {
  const query = useSearchParams()
  const router = useRouter()
  const locale = useLocale()
  // We need pathname only for better logs
  const pathname = usePathname()
  const { _slugs, _isHydrated } = useGlobalStore((state) => state.system)
  const editableQuery = new URLSearchParams(query)

  const { setCoopMembershipIdCode, setEuroBonusCode } = useGlobalStore(
    (state) => state.partnerFields,
  )

  const replacePathParams = (key: string) => {
    editableQuery.delete(key)

    // Make sure that there is a valid slugs array before replacing the route, otherwise incorrect redirect might be performed
    // Do not expect that we want to get some param for the homepage, if we do in future that has to be changed
    if (_slugs[locale].length > 0) {
      router.replace(
        {
          pathname: routes.GENERIC,
          params: { slug: _slugs[locale] },
          query: Object.fromEntries(editableQuery.entries()),
        },
        { scroll: false },
      )
    } else {
      logError(
        new PageError(`Cannot replace search param with key ${key}`, `Page path: ${pathname}`),
      )
    }
  }

  useEffect(
    () => {
      if (_isHydrated && query) {
        switch (true) {
          case editableQuery.has(queryMapping.eurobonus): {
            const eurobonusId = editableQuery.get(queryMapping.eurobonus)

            if (typeof eurobonusId === 'string') {
              setEuroBonusCode(eurobonusId)
            }

            replacePathParams(queryMapping.eurobonus)

            break
          }
          case editableQuery.has(queryMapping.coop): {
            const coopId = editableQuery.get(queryMapping.coop)

            if (typeof coopId === 'string') {
              setCoopMembershipIdCode(coopId)
            }
            editableQuery.delete(queryMapping.coop)
            replacePathParams(queryMapping.coop)

            break
          }
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [query, _isHydrated],
  )

  return null
}
